import {
  useGetAllActivities,
  useGetConfiguration,
  useLabelAssistInterrupt,
  useSyncActivity
} from 'api/sdk';
import { useAppSelector } from 'hooks/redux';
import { useConfirm } from 'plugins/confirm';
import { useSnack } from 'plugins/snack';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { SyncActivityRequest } from 'schemas';
import { RootState } from 'store';
import {
  addActivity,
  clearActivities,
  createActivity,
  deleteActivity,
  deleteAllClips,
  deleteSelectedClips,
  mergeActivities,
  moveImages,
  updateActivityName
} from 'store/config/activities';
import {
  deleteAllClipItems,
  selectCurrentCamera,
  setCurrentCameraId
} from 'store/config/configSlice';
import { selectCameras } from 'store/config/configSlice';

import { PipelineStatus } from 'types';

export const useActivityConfiguration = () => {
  const dispatch = useDispatch();
  const snack = useSnack();
  const configId = useSelector(
    (state: RootState) => state.configuration.configuration
  );
  const currentCamera = useSelector(selectCurrentCamera);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const confirm = useConfirm();
  const { mutateAsync: updateActivity, isLoading: isSyncLoading } =
    useSyncActivity();
  const [selectedActivities, setSelectedActivities] = useState<string[]>([]);

  const [selectedDestActivities, setSelectedDestActivities] = useState<
    string | null
  >(null);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [moveModelOpen, setMoveModelOpen] = useState(false);
  const cameras = useSelector(selectCameras);
  const [updateList, setUpdateList] = useState<boolean>(false);
  const [createModelOpen, setCreateModelOpen] = useState<{
    open: boolean;
    data?: {
      name: string;
    };
  }>({
    open: false
  });

  const currentCameraId = useSelector(
    (state: RootState) => state.configV2.currentCameraId
  );

  const { refetch: list, isLoading: isLoadingListConfig } = useGetConfiguration(
    {
      config_id: configId || ''
    },
    {
      query: {
        enabled: !!configId
      }
    }
  );

  const setCurrentCamera = (cameraId: string) => {
    dispatch(setCurrentCameraId(cameraId));
  };

  const { refetch, isLoading, error } = useGetAllActivities(configId || '', {
    pipeline_satus: true || null
  });

  const apiLoading = isLoading;
  const analyticsList = useSelector(
    (state: RootState) => state.configV2.analytics
  );

  const activities = useSelector((state: RootState) => state.activities.data);
  const activity = activities || {};

  const pipelineStatuses = useMemo(() => {
    const status: Array<{
      id: string;
      title: string;
      status: PipelineStatus;
      progress: number;
    }> = [];

    return status;
  }, [cameras, activities]);

  /**
   * Update the current camera
   */
  const handleCameraChange = (id: string) => {
    dispatch(setCurrentCameraId(id));
    setSelectedActivities([]);
  };

  useEffect(() => {
    if (configId || updateList) {
      refetch().then(response => {
        const responseData = response.data?.data;
        if (responseData && typeof responseData === 'object') {
          const dataArray = [responseData];

          dispatch(addActivity(dataArray));
        }
      });
    }
  }, [configId, updateList]);

  useEffect(() => {
    if (updateList) {
      dispatch(
        deleteAllClipItems({
          cameraId: currentCamera?.id?.toString() ?? ''
        })
      );
    }
  }, [updateList]);

  // refetchPipelineStatus().then(response => {
  //   dispatch(updatePipelineStatus(response.data?.data || []));
  // });

  const handleUpdateActivityName = (id: string, name: string) => {
    // dispatch(
    //   updateActivityName({
    //     cameraId: currentCameraId || '',
    //     ActivityId: id,
    //     name
    //   })
    // );
  };

  const handleMergeActivities = (new_name: string) => {
    const dest = selectedActivities[0];
    const src = selectedActivities.slice(1);
    // Merge objects
    dispatch(
      mergeActivities({
        cameraId: currentCameraId || '',
        srcId: src,
        destId: dest,
        name: new_name
      })
    );
    setSelectedActivities([]);
  };

  const handleMoveClips = (
    objectId: string,
    selectedDestActivities: string | null,
    clipsId: string[]
  ) => {
    const dest = selectedDestActivities;

    // move objects
    dispatch(
      moveImages({
        cameraId: currentCameraId || '',
        srcActivity: objectId,
        destActivity: dest,
        clips: clipsId
      })
    );
    setSelectedActivities([]);
    setMoveModelOpen(false);
  };

  const handleDeleteAllClips = (activityId: string) => {
    currentCameraId &&
      confirm({
        title: 'Delete all images',
        content: 'Are you sure you want to delete all images?'
      }).then(() => {
        dispatch(
          deleteAllClips({
            cameraId: currentCameraId,
            activityId
          })
        );
        setSelectedItems([]);
      });
  };

  const handleDeleteClip = (activityId: string, clipsIds: string[]) => {
    currentCameraId &&
      confirm({
        title: 'Delete images',
        content: `Are you sure you want to delete ${clipsIds.length} images?`
      }).then(() => {
        dispatch(
          deleteSelectedClips({
            cameraId: currentCameraId,
            activityId,
            clips: clipsIds
          })
        );
        setSelectedItems([]);
      });
  };

  const handleDeleteActivity = (activityId: string) => {
    currentCameraId &&
      confirm({
        title: 'Delete activity',
        content: `Are you sure you want to delete selected activity?`
      }).then(() => {
        dispatch(
          deleteActivity({
            activityId
          })
        );
      });
  };

  const handlerefresh = () => {
    refetch();
    // Incrementing the key to force a re-render
    setUpdateList(true);

    // Reset refreshKey to false after 3 seconds
    setTimeout(() => {
      setUpdateList(false);
    }, 1000);
  };

  const [disableInterrupt, setDisableInterrupt] = useState(false);

  const { mutateAsync: interrupt } = useLabelAssistInterrupt();

  const handleInterruptClick = (cameraId: string) => {
    setDisableInterrupt(true);
    interrupt({
      cameraId: cameraId?.toString() ?? '',
      configId: configId?.toString() ?? ''
    }).then(() => {
      snack({
        message: 'Activity updated successfully',
        severity: 'success'
      });
      setDisableInterrupt(false);
    });
  };

  const handleCreateActivity = (
    activityId: string | null,
    new_name: string,

    imageIds: string[]
  ) => {
    setSelectedActivities([]);
  };

  const syncActivity = (activityData: any): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
      if (!activity) {
        console.error('Objects array is null or undefined');
        return resolve();
      }
      // Prepare the data for the update
      const data: SyncActivityRequest[] = [];
      const mergedData = activities;
      mergedData.forEach((data, index) => {
        if (data?.id === activityData?.id) {
          mergedData[index] = activityData;
        }
      });

      mergedData.map((activity: any) => {
        data.push({
          activity: activity.activity,
          clips: activity.clips.map((clip: any) => ({
            camera_id: clip.camera_id,
            end_time: String(clip.end_time),
            id: clip.id,
            start_time: String(clip.start_time),
            thumbnail_path: clip.thumbnail_path
          })),
          configuration_id: configId || '',
          id: activity.id
        });
      });

      updateActivity({
        configId: configId || '',
        data: data,
        params: {
          video: currentCameraId || ''
        }
      })
        .then(() => {
          snack({
            message: 'Activity updated successfully',
            severity: 'success'
          });
          resolve();
        })
        .catch(() => {
          snack({
            message: 'Error updating activity',
            severity: 'error'
          });
          reject();
        });
    });
  };

  return {
    isLoading,
    refetch,
    setCurrentCamera,
    currentCameraId,
    cameras,
    activity,
    pipelineStatuses,
    selectedActivities,
    selectedItems,
    setSelectedItems,
    setSelectedActivities,
    handleCameraChange,
    apiLoading,
    handleDeleteActivity,
    handleUpdateActivityName,
    handleMergeActivities,
    handleDeleteAllClips,
    handleDeleteClip,

    selectedDestActivities,
    setSelectedDestActivities,
    handleMoveClips,
    moveModelOpen,
    setMoveModelOpen,
    currentCamera,
    videoContainerRef,
    handlerefresh,
    handleInterruptClick,
    disableInterrupt,
    setCreateModelOpen,
    createModelOpen,
    configId,
    handleCreateActivity,
    syncActivity,
    isSyncLoading,
    updateList,
    isLoadingListConfig
  };
};
